import { useMemo, useState } from "react";
import { Card } from "src/components/UI/Card";
import { Column, DataTable } from "src/components/UI/DataTable";
import { withEmptyState } from "src/components/UI/withEmptyState";
import EastIcon from '@mui/icons-material/East';
import { useKeywordAssist } from "../Providers/KAProviders";
import KADifficultyFilter from "./KADifficultyFilter";

import { KeywordAssistItem } from "../../types/keyword-assist.type";


interface IKeywordAssistDataProps {
  data: KeywordAssistItem[];
}

const EnhancedDataTable = withEmptyState(DataTable);

function KeywordAssistData(props: IKeywordAssistDataProps) {
  const { data } = props;

  const { changeCurrentView, setDetailedViewURL } = useKeywordAssist();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<KeywordAssistItem['difficulty'] | 'All'>('All');

  // Handle local searching/filtering
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        item.keyword.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.url.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDifficulty =
        selectedDifficulty === 'All' || item.difficulty === selectedDifficulty;
      return matchesSearch && matchesDifficulty;
    });
  }, [data, searchQuery, selectedDifficulty]);

  // Define Columns
  const columns: Column<KeywordAssistItem>[] = useMemo(
    () => [
      {
        id: 'url',
        label: <span className="!text-sm">Page URL</span>,
        sortable: false,
        onClick: (row) => {
          setDetailedViewURL(row.url);
          changeCurrentView('detailed')
        },
        render: (row) => (
          <>
            <p className="text-[var(--color-purple-strong)] text-sm font-medium inline-block mr-2">
              {row.url}
            </p>
            <p className={[
              "inline-block px-2 py-1 rounded-lg capitalize font-medium text-xs",
              row.difficulty === 'strong' && "text-[#1A927B] bg-[#ECFDF3]",
              row.difficulty === 'moderate' && "text-[#C4320A] bg-[#FFF6ED]",
              row.difficulty === 'weak' && "text-[#EF4444] bg-[#FEF3F2]"
            ].join(" ")}>
              {row.difficulty}
            </p>
          </>
        )
      },
      {
        id: 'status',
        label: <span className="!text-sm">Action</span>,
        sortable: false,
        width: 150,
        onClick: (row) => {
          setDetailedViewURL(row.url);
          changeCurrentView('detailed')
        },
        render: () => {
          return (
            <>
              <p className="text-primary text-sm font-medium inline-block mr-2">
                View Details
              </p>
              <EastIcon sx={{ fontSize: "18px" }} />
            </>
          );
        }
      }
    ],
    []
  );
  return (
    <>
      <Card>
        <div className="px-6">
          <div className='mb-4'>
            <h3 className="text-2xl font-bold text-primary">Keyword Assist Overview</h3>
            <p className="text-sm text-[#8E8EA9] mt-1">
              Analyze keyword performance, difficulty, and optimization status across your pages.
            </p>
          </div>
          <div className="gap-4 mb-2">
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search keyword or URL..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 pl-4 pr-10 py-2 w-64 text-sm bg-white border border-[#E6E6F2] rounded-lg focus:outline-none focus:border-[var(--color-text-purple)] transition-colors duration-200"
                />
                <svg
                  className="absolute right-3 top-2.5 h-4 w-4 text-[#8E8EA9]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {/* Filters */}
              <KADifficultyFilter
                data={data}
                selectedDifficulty={selectedDifficulty}
                onChange={setSelectedDifficulty}
              />
            </div>
          </div>

          <EnhancedDataTable
            data={filteredData}
            columns={columns}
            getRowId={(row) => row.id}
            pagination={true}
            defaultRowsPerPage={10}
            emptyTitle="Data not available"
            emptyDescription='No records match your filters. Try adjusting your search query or difficulty filters to see results.'
          />
        </div>
      </Card>
    </>
  )
}

export default KeywordAssistData